import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth'
import { doc, onSnapshot, getDoc, setDoc } from 'firebase/firestore'
import { auth, db } from '../lib/firebase'

const AuthContext = createContext({
  user: null,
  userData: null,
  loading: true,
  isAuthenticated: false,
  signOut: async () => {},
})

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser)

      if (firebaseUser) {
        // Try to get user data from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))

          if (userDoc.exists()) {
            setUserData(userDoc.data())
          } else {
            // Create basic user profile if it doesn't exist
            const newUserData = {
              email: firebaseUser.email,
              displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0],
              createdAt: new Date().toISOString(),
              lastActive: new Date().toISOString(),
              // Analytics tracking fields
              favoriteTeams: [],
              gamesViewed: 0,
              analysisViewed: 0,
              // Engagement fields
              xp: 0,
              level: 1,
              achievements: [],
              loginStreak: 0,
              completedLessons: []
            }

            await setDoc(doc(db, 'users', firebaseUser.uid), newUserData)
            setUserData(newUserData)
          }
        } catch (error) {
          console.error('Error fetching user data:', error)
          // Even if Firestore fails, user is still authenticated
          setUserData({
            email: firebaseUser.email,
            displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0]
          })
        }
      } else {
        setUserData(null)
      }

      setLoading(false)
    })

    return () => unsubscribeAuth()
  }, [])

  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth)
      setUser(null)
      setUserData(null)
    } catch (error) {
      console.error('Error signing out:', error)
      throw error
    }
  }, [])

  const value = {
    user,
    userData,
    loading,
    isAuthenticated: !!user,
    signOut,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext